import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import adminApi from '../lib/adminApi';

const RANGES = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
];

const Reports = () => {
  const [range, setRange] = useState('30d');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    adminApi.get(`/analytics/daily?range=${range}`)
      .then(({ data }) => mounted && setItems(data.items || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load reports'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [range]);

  const handleExport = async (fmt) => {
    try {
      const resp = await adminApi.get(`/export/daily?fmt=${fmt}&range=${range}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily-report.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Reports</h1>
        <p className="admin-page-sub">Daily breakdown of leads & engagement</p>
      </header>

      <div className="admin-toolbar">
        <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="report-range">
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>

        <div className="admin-toolbar-right">
          <button onClick={() => handleExport('csv')} className="admin-btn-secondary" data-testid="report-export-csv">
            <Download size={14} /> CSV
          </button>
          <button onClick={() => handleExport('xlsx')} className="admin-btn-secondary" data-testid="report-export-xlsx">
            <Download size={14} /> Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          <div className="admin-chart-card" data-testid="report-chart">
            <h3>Activity Trend</h3>
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={items} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: '#a0a0a0', fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: '#a0a0a0', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333' }} />
                <Legend />
                <Line type="monotone" dataKey="leads" name="Lead Forms" stroke="#D4AF37" strokeWidth={2} />
                <Line type="monotone" dataKey="whatsapp_clicks" name="WhatsApp Clicks" stroke="#25D366" strokeWidth={2} />
                <Line type="monotone" dataKey="phone_clicks" name="Phone Clicks" stroke="#F4D03F" strokeWidth={2} strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table" data-testid="report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Lead Forms</th>
                  <th>WhatsApp Clicks</th>
                  <th>Phone Clicks</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.date}>
                    <td>{r.date}</td>
                    <td>{r.leads}</td>
                    <td>{r.whatsapp_clicks}</td>
                    <td>{r.phone_clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
